<script lang="ts">
import { Options, Vue } from 'vue-class-component';
import { Ref } from '@/helpers/Decorators';
import { KeyValuePair, Option } from '@/helpers/Interfaces';
import { Form } from '@/helpers/Form';
import { $t } from '@/plugins/localization';
import { BpmnEmitOptions } from '@/modules/low-code/process/services/BpmnService';
import { FrontActionModal } from '@/components/common/dynamic-grid/helpers/GridEnums';
import ProcessService, { ProcessState, SetTargetsModel } from '@/modules/low-code/process/services/ProcessService';
import IdeoModal from '@/components/ideo/modal/IdeoModal.vue';

@Options({
    emits: ['reload'],
})
export default class SetTargetsModal extends Vue
{
    @Ref('modal') public modal: () => IdeoModal;

    public selectedStep: KeyValuePair = null;
    public processState: ProcessState = null;

    public form = Form.create<SetTargetsModel>({
        userTargets: [],
        unitTargets: [],
        teamTargets: [],
        groupTargets: [],
        roleName: '',
        targetId: 0,
    });

    public action = Form.create<BpmnEmitOptions>({
        id: '',
        licence: '',
        actionName: '',
        buttonId: '',
        displayMode: 'Modal',
        displaySize: 'Medium',
        title: '',
        buttonName: $t('[[[Zmiana akceptantów]]]'),
        buttonVariant: 'success',
        buttonIcon: '',
        groupAction: false,
    });

    public get currentStepOptions(): KeyValuePair[]
    {
        if (this.processState != null) return this.processState.currentSteps.map((p) => p.step);

        return [];
    }

    public mounted(): void
    {
        this.$events.$on(FrontActionModal.SetTargetsModal, (action: BpmnEmitOptions) =>
        {
            this.action.withData(action);
            this.loadDetails();
            this.modal().show();
        });
    }

    public unmounted(): void
    {
        this.$events.$off(FrontActionModal.SetTargetsModal);
    }

    public async accept(): Promise<void>
    {
        try
        {
            this.form.wait();

            await ProcessService.setProcessTargets(this.action.licence, this.action.id, this.form.formatData());
            this.$alert.success(this.$t('[[[Akcja wykonana pomyślnie]]]'));
            this.modal().hide();

            setTimeout(() =>
            {
                this.$emit('reload');
                this.$events.$emit('refetchActions');
                this.form.reset();
            }, 500);
            this.form.clear();
        }
        catch (ex)
        {
            this.handleException(ex, {
                400: (ex: any) => this.$alert.warning(ex.message),
                422: (ex: any) => [
                    this.form.$errors.record(ex.data.errors),
                    this.$alert.warning(this.$t('[[[Nie wszystkie pola zostały wypełnione prawidłowo.]]]')),
                ],
            });
        }
        finally
        {
            this.form.continue();
        }
    }

    public async loadDetails(): Promise<boolean>
    {
        if (!this.action.id) return false;

        try
        {
            const response = await ProcessService.getProcessCurrentSteps(this.action.licence, this.action.id);

            this.processState = response;
            this.selectedStep = this.processState.currentSteps[0].step;
            this.form.targetId = this.processState.currentSteps[0].targetId;
            this.form.roleName = this.processState.currentSteps[0].roleName;
            this.form.userTargets = this.processState.currentSteps[0].userTargets as any;
            this.form.unitTargets = this.processState.currentSteps[0].unitTargets as any;
            this.form.teamTargets = this.processState.currentSteps[0].teamTargets as any;
            this.form.groupTargets = this.processState.currentSteps[0].groupTargets as any;
        }
        catch (ex)
        {
            return false;
        }

        return true;
    }

    public onStepChanged(value: Option): void
    {
        if (value)
        {
            const step = this.processState.currentSteps.find((p) => p.step.key == (value as any).key);

            this.form.targetId = step.targetId;
            this.form.roleName = step.roleName;
            this.form.targets = step.targets as any;
        }
        else
        {
            this.form.targetId = null;
        }
    }
}
</script>

<template>
    <IdeoModal ref="modal" :title="action.title" centered>
        <template #default>
            <IdeoFormGroup
                :label="$t('[[[Krok]]]')"
                :invalid-feedback="form.$errors.first('targetId')"
                :state="form.$errors.state('targetId')"
                required
            >
                <IdeoSelect v-model="selectedStep" :options="currentStepOptions" @update:modelValue="onStepChanged" />
            </IdeoFormGroup>
            <IdeoFormGroup v-if="selectedStep" :label="$t('[[[Użytkownicy]]]')">
                <IdeoSelect
                    v-model="form.userTargets"
                    :endpoint="`simple-dictionary/${action.licence}/form/${action.id}/bpmn-set-targets/userTargets`"
                    :multiselect="true"
                />
            </IdeoFormGroup>
            <IdeoFormGroup v-if="selectedStep" :label="$t('[[[Działy]]]')">
                <IdeoSelect
                    v-model="form.unitTargets"
                    :endpoint="`simple-dictionary/${action.licence}/form/${action.id}/bpmn-set-targets/unitTargets`"
                    :multiselect="true"
                />
            </IdeoFormGroup>
            <IdeoFormGroup v-if="selectedStep" :label="$t('[[[Zespoły]]]')">
                <IdeoSelect
                    v-model="form.teamTargets"
                    :endpoint="`simple-dictionary/${action.licence}/form/${action.id}/bpmn-set-targets/teamTargets`"
                    :multiselect="true"
                />
            </IdeoFormGroup>
            <IdeoFormGroup v-if="selectedStep" :label="$t('[[[Grupy użytkowników]]]')">
                <IdeoSelect
                    v-model="form.groupTargets"
                    :endpoint="`simple-dictionary/${action.licence}/form/${action.id}/bpmn-set-targets/groupTargets`"
                    :multiselect="true"
                />
            </IdeoFormGroup>
        </template>
        <template #modal-footer="{ cancel }">
            <IdeoButton name="btnConfirmAccept" variant="success" @click="accept">
                {{ $t('[[[Zapisz]]]') }}
            </IdeoButton>
            <IdeoButton variant="secondary" @click="cancel">
                {{ $t('[[[Anuluj]]]') }}
            </IdeoButton>
        </template>
    </IdeoModal>
</template>
